import React, { useMemo, useState } from "react";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { VStack } from "@design-system/VStack/VStack";
import { NumberControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";

const searchItems = [
    "Bank reconciliation",
    "Chart of accounts",
    "Invoice matching",
    "Monthly close",
    "Transaction review",
];

const toNonNegativeNumber = (value: number) =>
    Number.isFinite(value) ? Math.max(0, value) : undefined;

const filterItems = (query: string | undefined) => {
    if (!query) {
        return searchItems;
    }

    const normalizedQuery = query.toLowerCase();
    return searchItems.filter((item) =>
        item.toLowerCase().includes(normalizedQuery),
    );
};

export const SearchFieldsPlayground = () => {
    const [searchFilterValue, setSearchFilterValue] = useState<
        string | undefined
    >();
    const [minLength, setMinLength] = useState(3);
    const [debounceTime, setDebounceTime] = useState(300);

    const searchFilterItems = useMemo(
        () => filterItems(searchFilterValue),
        [searchFilterValue],
    );

    return (
        <SandboxLayout
            controls={
                <section>
                    <h2>SearchFilter behavior</h2>
                    <VStack gap={16}>
                        <NumberControl
                            label="Minimum length"
                            min={0}
                            value={minLength}
                            onChange={(value) => {
                                const nextMinLength =
                                    toNonNegativeNumber(value);
                                if (nextMinLength !== undefined) {
                                    setMinLength(nextMinLength);
                                }
                            }}
                        />
                        <NumberControl
                            label="Debounce (ms)"
                            min={0}
                            step={100}
                            value={debounceTime}
                            onChange={(value) => {
                                const nextDebounceTime =
                                    toNonNegativeNumber(value);
                                if (nextDebounceTime !== undefined) {
                                    setDebounceTime(nextDebounceTime);
                                }
                            }}
                        />
                    </VStack>
                </section>
            }
        >
            <ShowcasePreview
                summary="Debounced"
                output={JSON.stringify(
                    {
                        SearchFilter: {
                            filter: searchFilterValue ?? null,
                            minLength,
                            debounceTime,
                        },
                    },
                    null,
                    2,
                )}
            >
                <section className={styles.surface}>
                    <div className={styles.panelContent}>
                        <h3>SearchFilter</h3>
                        <VStack gap={12}>
                            <div>
                                <SearchFilter
                                    filter={searchFilterValue}
                                    onChange={setSearchFilterValue}
                                    minLength={minLength}
                                    debounceTime={debounceTime}
                                    placeholder="Search workflows..."
                                />
                            </div>
                            <ul>
                                {searchFilterItems.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </VStack>
                    </div>
                </section>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
